import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Phone, MessageCircle, Paperclip, X } from "lucide-react";
import { PHONE_NUMBER, WHATSAPP_NUMBER } from "@/constants/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHONE_TEL = PHONE_NUMBER ? `tel:${PHONE_NUMBER.replace(/[^+\d]/g, "")}` : null;
const WHATSAPP_URL = WHATSAPP_NUMBER
  ? `https://wa.me/${WHATSAPP_NUMBER.replace(/\D/g, "")}`
  : null;

const ACCEPTED_FILES = ".jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.zip,.stl,.obj,.step,.stp,.ai,.psd,.eps";

const SERVICE_OPTIONS = [
  { id: "3d-print-build", label: "3D Print / Build" },
  { id: "web-launch", label: "Web & Launch" },
  { id: "media-assets", label: "Media Assets" },
  { id: "product-launch", label: "Product Launch" },
  { id: "marketing", label: "Marketing" },
  { id: "brand-assets", label: "Brand Assets" },
];

export default function QuoteForm() {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");
  const [services, setServices] = useState(["3d-print-build", "web-launch"]);
  const [requestType, setRequestType] = useState("quote");
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    setFiles((prev) => [...prev, ...incoming].slice(0, 10));
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    const onConsult = () => setRequestType("consultation");
    window.addEventListener("goat:consultation", onConsult);
    return () => window.removeEventListener("goat:consultation", onConsult);
  }, []);

  const toggleService = (id) => {
    setServices((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !contact.trim()) {
      toast.error("Please add your project name and a contact method.");
      return;
    }
    setSubmitting(true);
    try {
      let attachment_ids = [];
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        const { data: uploadData } = await axios.post(`${API}/uploads`, formData);
        attachment_ids = uploadData.files.map((f) => f.id);
      }
      await axios.post(`${API}/quotes`, {
        name: name.trim(),
        contact: contact.trim(),
        services,
        message: message.trim() || null,
        request_type: requestType,
        attachment_ids,
      });
      toast.success(
        requestType === "consultation"
          ? "Consultation request received. We will reach out to schedule your slot."
          : "Quote request received. Our production team will respond shortly."
      );
      setName("");
      setContact("");
      setMessage("");
      setServices([]);
      setFiles([]);
      setRequestType("quote");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your request. Please try again or reach us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-24 px-6 lg:px-12 max-w-4xl mx-auto">
      <div className="border-2 border-[#45f3ff]/40 bg-[#0e121c] p-8 md:p-12 shadow-[0_0_50px_rgba(69,243,255,0.1)]">
        <div className="text-center max-w-lg mx-auto mb-10">
          <span className="font-mono-goat text-xs uppercase tracking-widest text-[#45f3ff]">
            Direct Project Entry
          </span>
          <h2 className="font-display text-4xl uppercase tracking-tight text-white mt-1">
            Initiate Your Build
          </h2>
          <p className="text-neutral-400 text-xs mt-2">
            Tell us your digital and physical requirements for an immediate quote calculation.
          </p>
        </div>

        <form className="space-y-5" onSubmit={handleSubmit} data-testid="quote-form">
          <div className="flex gap-2 justify-center" data-testid="request-type-toggle">
            {[
              { id: "quote", label: "Request a Quote" },
              { id: "consultation", label: "Book a Consultation" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`request-type-${t.id}`}
                onClick={() => setRequestType(t.id)}
                className={`px-5 py-2.5 font-mono-goat text-xs uppercase tracking-wider border transition-colors ${
                  requestType === t.id
                    ? "bg-[#45f3ff] text-black border-[#45f3ff] font-bold"
                    : "bg-transparent text-neutral-400 border-[#24283b] hover:border-[#45f3ff]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="quote-name"
                className="block font-mono-goat text-[11px] uppercase tracking-wider text-neutral-300 mb-2"
              >
                Project Name / Founder
              </label>
              <input
                id="quote-name"
                data-testid="quote-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#161a29] border border-[#24283b] text-white px-4 py-3 text-sm focus:border-[#45f3ff] focus:outline-none transition-colors"
                placeholder="e.g. Apex Hardware Labs"
              />
            </div>
            <div>
              <label
                htmlFor="quote-contact"
                className="block font-mono-goat text-[11px] uppercase tracking-wider text-neutral-300 mb-2"
              >
                Contact Method
              </label>
              <input
                id="quote-contact"
                data-testid="quote-contact-input"
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                className="w-full bg-[#161a29] border border-[#24283b] text-white px-4 py-3 text-sm focus:border-[#45f3ff] focus:outline-none transition-colors"
                placeholder="Email or WhatsApp"
              />
            </div>
          </div>

          <div>
            <label className="block font-mono-goat text-[11px] uppercase tracking-wider text-neutral-300 mb-2">
              Service Matrix Scope
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="service-scope-options">
              {SERVICE_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  data-testid={`service-option-${opt.id}`}
                  className="flex items-center gap-2 border border-[#24283b] p-3 text-xs text-neutral-300 cursor-pointer bg-[#141824] hover:border-[#45f3ff] transition-colors"
                >
                  <input
                    type="checkbox"
                    className="accent-[#45f3ff]"
                    checked={services.includes(opt.id)}
                    onChange={() => toggleService(opt.id)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="quote-message"
              className="block font-mono-goat text-[11px] uppercase tracking-wider text-neutral-300 mb-2"
            >
              Project Details
            </label>
            <textarea
              id="quote-message"
              data-testid="quote-message-input"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full bg-[#161a29] border border-[#24283b] text-white px-4 py-3 text-sm focus:border-[#45f3ff] focus:outline-none transition-colors resize-none"
              placeholder="Timeline, quantities, storefront goals..."
            />
          </div>

          <div>
            <label className="block font-mono-goat text-[11px] uppercase tracking-wider text-neutral-300 mb-2">
              Attachments (optional — logos, briefs, 3D files)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILES}
              data-testid="quote-file-input"
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              data-testid="quote-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 border border-dashed border-[#24283b] px-4 py-3 font-mono-goat text-xs uppercase tracking-wider text-neutral-400 hover:border-[#45f3ff] hover:text-white transition-colors w-full justify-center"
            >
              <Paperclip className="w-3.5 h-3.5" /> Attach files (images, PDF, ZIP, STL, STEP, AI, PSD, EPS — max 25MB each)
            </button>
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="quote-file-list">
                {files.map((f, i) => (
                  <span
                    key={`${f.name}-${i}`}
                    className="inline-flex items-center gap-2 border border-[#24283b] bg-[#141824] px-3 py-1.5 font-mono-goat text-[11px] text-neutral-300"
                  >
                    {f.name}
                    <button
                      type="button"
                      data-testid={`quote-file-remove-${i}`}
                      onClick={() => removeFile(i)}
                      className="text-neutral-500 hover:text-red-400 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
            <button
              type="submit"
              data-testid="quote-submit-btn"
              disabled={submitting}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#45f3ff] text-black font-mono-goat font-bold text-xs uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  Transmitting <Loader2 className="w-3.5 h-3.5 animate-spin" />
                </>
              ) : (
                <>
                  {requestType === "consultation" ? "Book Consultation" : "Submit Quote Request"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
            {(WHATSAPP_URL || PHONE_TEL) && (
              <div className="flex items-center gap-4 text-xs font-mono-goat text-neutral-400">
                <span>Or via direct link:</span>
                {WHATSAPP_URL && (
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-chat-link"
                    className="inline-flex items-center gap-1 text-[#45f3ff] underline hover:text-white transition-colors"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp Chat
                  </a>
                )}
                {WHATSAPP_URL && PHONE_TEL && <span>•</span>}
                {PHONE_TEL && (
                  <a
                    href={PHONE_TEL}
                    data-testid="direct-call-link"
                    className="inline-flex items-center gap-1 text-[#45f3ff] underline hover:text-white transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5" /> Direct Call
                  </a>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
